import React, { useState, useEffect } from 'react'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import EmptyTable from './EmptyTable'
import { useAuth } from "./api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'
import useMobile from './hooks/useMobile'
import MobilePaymentsTable from './components/MobilePaymentsTable'
import LoadingSpinner from './components/LoadingSpinner'

// Компонент пагинации
const PaginationActions = ({ page, rowsPerPage, count, onPageChange, loadingButton }) => {
    const safePage = isNaN(page) ? 0 : page
    const safeRowsPerPage = isNaN(rowsPerPage) ? 10 : rowsPerPage
    const safeCount = isNaN(count) ? 0 : count
    
    const totalPages = safeRowsPerPage === 0 ? 0 : Math.ceil(safeCount / safeRowsPerPage)
    
    const handleFirstPage = () => onPageChange(null, 0, 'first')
    const handlePrevPage = () => onPageChange(null, Math.max(0, safePage - 1), 'prev')
    const handleNextPage = () => onPageChange(null, Math.min(totalPages - 1, safePage + 1), 'next')
    const handleLastPage = () => onPageChange(null, Math.max(0, totalPages - 1), 'last')
    
    return (
        <div style={{ display: 'flex', gap: '4px' }}>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: (safePage === 0 || loadingButton) ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: (safePage === 0 || loadingButton) ? 0.5 : 1
                }}
                onClick={handleFirstPage}
                disabled={safePage === 0 || loadingButton}
            >
                {loadingButton === 'first' ? <LoadingSpinner /> : <KeyboardDoubleArrowLeft style={{ fontSize: '14px' }} />}
            </button>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: (safePage === 0 || loadingButton) ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: (safePage === 0 || loadingButton) ? 0.5 : 1
                }}
                onClick={handlePrevPage}
                disabled={safePage === 0 || loadingButton}
            >
                {loadingButton === 'prev' ? <LoadingSpinner /> : <KeyboardArrowLeft style={{ fontSize: '14px' }} />}
            </button>
            <span style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                margin: '0 var(--spacing-sm)',
                whiteSpace: 'nowrap',
                minWidth: '80px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center'
            }}>
                {safePage + 1} of {totalPages}
            </span>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: (safePage >= totalPages - 1 || loadingButton) ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: (safePage >= totalPages - 1 || loadingButton) ? 0.5 : 1
                }}
                onClick={handleNextPage}
                disabled={safePage >= totalPages - 1 || loadingButton}
            >
                {loadingButton === 'next' ? <LoadingSpinner /> : <KeyboardArrowRight style={{ fontSize: '14px' }} />}
            </button>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: (safePage >= totalPages - 1 || loadingButton) ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: (safePage >= totalPages - 1 || loadingButton) ? 0.5 : 1
                }}
                onClick={handleLastPage}
                disabled={safePage >= totalPages - 1 || loadingButton}
            >
                {loadingButton === 'last' ? <LoadingSpinner /> : <KeyboardDoubleArrowRight style={{ fontSize: '14px' }} />}
            </button>
        </div>
    )
}


// Компонент чипа для типа транзакции
const TransactionChip = ({ type }) => {
    const getChipStyle = (type) => {
        switch (type) {
            case 'lease':
                return {
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    color: '#FF9800',
                    border: '1px solid #FF9800'
                }
            case 'refund':
                return {
                    backgroundColor: 'rgba(0, 178, 74, 0.1)',
                    color: '#00B24A',
                    border: '1px solid #00B24A'
                }
            case 'deposit':
                return {
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    color: '#1976D2',
                    border: '1px solid #1976D2'
                }
            default:
                return {
                    backgroundColor: 'var(--medium-gray)',
                    color: 'var(--text-muted)',
                    border: '1px solid var(--light-gray)'
                }
        }
    }

    return (
        <span style={{
            ...getChipStyle(type),
            padding: '6px 12px',
            borderRadius: '7px',
            fontSize: 'var(--text-xs)',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100px',
            height: '35px',
            cursor: 'default',
            position: 'relative',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            {type}
        </span>
    )
}

// Компонент чипа для фильтрации по типу транзакций
const TransactionFilterChip = ({ type, isActive, onClick }) => {
    const getChipStyle = (type, isActive) => {
        const baseStyle = {
            padding: '6px 12px',
            borderRadius: '7px',
            fontSize: 'var(--text-xs)',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100px',
            height: '35px',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            boxSizing: 'border-box',
            transition: 'all var(--transition-normal)'
        }

        if (isActive) {
            switch (type) {
                case 'deposit':
                    return {
                        ...baseStyle,
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        color: '#1976D2',
                        border: '1px solid #1976D2'
                    }
                case 'refund':
                    return {
                        ...baseStyle,
                        backgroundColor: 'rgba(255, 152, 0, 0.1)',
                        color: '#FF9800',
                        border: '1px solid #FF9800'
                    }
                case 'lease':
                    return {
                        ...baseStyle,
                        backgroundColor: 'rgba(0, 178, 74, 0.1)',
                        color: '#00B24A',
                        border: '1px solid #00B24A'
                    }
                default:
                    return {
                        ...baseStyle,
                        backgroundColor: 'var(--medium-gray)',
                        color: 'var(--text-muted)',
                        border: '1px solid var(--light-gray)'
                    }
            }
        } else {
            return {
                ...baseStyle,
                backgroundColor: 'transparent',
                color: 'var(--text-muted)',
                border: '1px solid var(--light-gray)'
            }
        }
    }

    return (
        <span 
            style={getChipStyle(type, isActive)}
            onClick={onClick}
        >
            {type}
        </span>
    )
}


// Компонент DatePicker с кастомным календарем
const DatePicker = ({ label, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [tempDate, setTempDate] = useState(value)

    // Закрытие календаря при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.date-picker-container')) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const formatDisplayDate = (date) => {
        return date.format('MM/DD/YYYY')
    }

    const handleDateSelect = (selectedDate) => {
        setTempDate(selectedDate)
        onChange(selectedDate)
        setIsOpen(false)
    }

    const handleClear = () => {
        const clearedDate = dayjs()
        setTempDate(clearedDate)
        onChange(clearedDate)
        setIsOpen(false)
    }

    const handleToday = () => {
        const today = dayjs()
        setTempDate(today)
        onChange(today)
        setIsOpen(false)
    }

    const getCalendarDays = () => {
        const startOfMonth = tempDate.startOf('month')
        const endOfMonth = tempDate.endOf('month')
        const startOfCalendar = startOfMonth.startOf('week')
        const endOfCalendar = endOfMonth.endOf('week')

        const days = []
        let current = startOfCalendar

        while (current.isBefore(endOfCalendar, 'day') || current.isSame(endOfCalendar, 'day')) {
            days.push({
                date: current,
                isCurrentMonth: current.isSame(tempDate, 'month'),
                isSelected: current.isSame(value, 'day'),
                isToday: current.isSame(dayjs(), 'day')
            })
            current = current.add(1, 'day')
        }

        return days
    }

    const navigateMonth = (direction) => {
        const newDate = direction === 'prev' 
            ? tempDate.subtract(1, 'month')
            : tempDate.add(1, 'month')
        setTempDate(newDate)
    }

    const navigateYear = (direction) => {
        const newDate = direction === 'prev' 
            ? tempDate.subtract(1, 'year')
            : tempDate.add(1, 'year')
        setTempDate(newDate)
    }

    return (
        <div className="form-group date-picker-container" style={{ width: '200px', position: 'relative' }}>
            <label className="form-label">{label}</label>
            <div 
                className="form-input"
                onClick={() => setIsOpen(!isOpen)}
                style={{ 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <span>{formatDisplayDate(value)}</span>
                <span style={{ color: 'var(--primary-orange)' }}>📅</span>
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    background: 'var(--medium-gray)',
                    border: '1px solid var(--light-gray)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-md)',
                    boxShadow: 'var(--shadow-card)',
                    marginTop: '4px',
                    width: '280px',
                    minWidth: '280px'
                }}>
                    {/* Заголовок календаря */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--spacing-sm)',
                        gap: '4px'
                    }}>
                        <button
                            onClick={() => navigateYear('prev')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ⟪
                        </button>
                        <button
                            onClick={() => navigateMonth('prev')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ‹
                        </button>
                        <span style={{
                            fontSize: 'var(--text-sm)',
                            fontWeight: '600',
                            color: 'var(--text-light)',
                            flex: 1,
                            textAlign: 'center'
                        }}>
                            {tempDate.format('MMMM YYYY')}
                        </span>
                        <button
                            onClick={() => navigateMonth('next')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ›
                        </button>
                        <button
                            onClick={() => navigateYear('next')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ⟫
                        </button>
                    </div>

                    {/* Дни недели */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px',
                        marginBottom: 'var(--spacing-sm)'
                    }}>
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                            <div key={day} style={{
                                padding: '8px 4px',
                                textAlign: 'center',
                                fontSize: 'var(--text-xs)',
                                fontWeight: '600',
                                color: 'var(--text-muted)'
                            }}>
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Календарная сетка */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px'
                    }}>
                        {getCalendarDays().map((day, index) => (
                            <button
                                key={index}
                                onClick={() => handleDateSelect(day.date)}
                                style={{
                                    padding: '8px 4px',
                                    border: 'none',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--text-sm)',
                                    cursor: 'pointer',
                                    background: day.isSelected 
                                        ? 'var(--primary-orange)' 
                                        : day.isToday 
                                            ? 'var(--dark-gray)' 
                                            : 'transparent',
                                    color: day.isSelected 
                                        ? 'var(--white)' 
                                        : !day.isCurrentMonth 
                                            ? 'var(--text-muted)' 
                                            : day.isToday 
                                                ? 'var(--primary-orange)' 
                                                : 'var(--text-light)',
                                    fontWeight: day.isSelected || day.isToday ? '600' : '400',
                                    transition: 'var(--transition-normal)'
                                }}
                                onMouseEnter={(e) => {
                                    if (!day.isSelected) {
                                        e.target.style.backgroundColor = 'var(--dark-gray)'
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!day.isSelected) {
                                        e.target.style.backgroundColor = day.isToday ? 'var(--dark-gray)' : 'transparent'
                                    }
                                }}
                            >
                                {day.date.format('D')}
                            </button>
                        ))}
                    </div>

                    {/* Кнопки действий */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 'var(--spacing-sm)',
                        paddingTop: 'var(--spacing-sm)',
                        borderTop: '1px solid var(--light-gray)',
                        gap: 'var(--spacing-sm)'
                    }}>
                        <button
                            onClick={handleClear}
                            style={{
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                flex: 1,
                                transition: 'var(--transition-normal)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.borderColor = 'var(--primary-orange)'
                                e.target.style.color = 'var(--primary-orange)'
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.borderColor = 'var(--light-gray)'
                                e.target.style.color = 'var(--text-light)'
                            }}
                        >
                            Clear
                        </button>
                        <button
                            onClick={handleToday}
                            style={{
                                background: 'var(--primary-orange)',
                                border: '1px solid var(--primary-orange)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--white)',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                flex: 1,
                                transition: 'var(--transition-normal)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'var(--dark-orange)'
                                e.target.style.borderColor = 'var(--dark-orange)'
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'var(--primary-orange)'
                                e.target.style.borderColor = 'var(--primary-orange)'
                            }}
                        >
                            Today
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}


// Десктопная версия компонента
const DesktopPaymentsTable = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [paymentsList, setPaymentsList] = useState([])
    const [startDate, setStartDate] = useState(() => dayjs())
    const [endDate, setEndDate] = useState(() => dayjs().endOf('month'))
    const [activeFilter, setActiveFilter] = useState(null) // null = все, 'deposit', 'refund', 'lease'
    const [loadingButton, setLoadingButton] = useState(null) // 'first', 'prev', 'next', 'last'

    // CSS стили для толстой полосы прокрутки
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            /* Адаптивная полоса прокрутки для таблицы PaymentsTable */
            .payments-table-container::-webkit-scrollbar {
                height: 24px; /* В 3 раза толще стандартной */
            }
            
            .payments-table-container::-webkit-scrollbar-track {
                background: var(--dark-gray);
                border-radius: 12px;
            }
            
            /* Светлая тема - оранжевая полоса */
            [data-theme="light"] .payments-table-container::-webkit-scrollbar-thumb {
                background: var(--primary-orange);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="light"] .payments-table-container::-webkit-scrollbar-thumb:hover {
                background: var(--secondary-orange);
            }
            
            /* Темная тема - светлая серая полоса */
            [data-theme="dark"] .payments-table-container::-webkit-scrollbar-thumb,
            :root:not([data-theme="light"]) .payments-table-container::-webkit-scrollbar-thumb {
                background: var(--light-gray);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="dark"] .payments-table-container::-webkit-scrollbar-thumb:hover,
            :root:not([data-theme="light"]) .payments-table-container::-webkit-scrollbar-thumb:hover {
                background: #B0B0B0;
            }
            
            /* Для Firefox - светлая тема */
            [data-theme="light"] .payments-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--primary-orange) var(--dark-gray);
            }
            
            /* Для Firefox - темная тема */
            [data-theme="dark"] .payments-table-container,
            :root:not([data-theme="light"]) .payments-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--light-gray) var(--dark-gray);
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])


    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - paymentsList?.txRecords?.length) : 0

    const handleChangePage = React.useCallback((event, newPage, buttonType = null) => {
        const validPage = isNaN(newPage) ? 0 : Math.max(0, newPage)
        
        // Предотвращаем множественные клики
        if (loadingButton) return
        
        setLoadingButton(buttonType)
        setPage(validPage)
    }, [loadingButton])

    const handleChangeRowsPerPage = React.useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }, [])
    // console.log('START:'+startDate)


    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD 00:00:00')
            const dateEndFormated = endDate.format('YYYY-MM-DD 23:59:59')
            
            // Получаем токен один раз
            const token = await getAccessTokenSilently()
            
            const paymentsPesponse = await props.apiClient.setToken(token).getUserPayments(dateStartFormated, dateEndFormated, page, rowsPerPage)
            setPaymentsList(paymentsPesponse)
            setLoadingButton(null)
          } catch (e) {
              setLoadingButton(null)
              // console.error(e)
          }
          
      }
     
      callAsync()   
    },[startDate, endDate, page, rowsPerPage, getAccessTokenSilently])

    const handleSetStartDate = React.useCallback((date) => {
      setStartDate(date)
    }, [])
  
    const handleSetEndDate = React.useCallback((date) => {
      setEndDate(date)
    }, [])

    return (
        <div>
            <h1 style={{ 
                margin: '0 0 var(--spacing-lg) 0',
                fontWeight: 600,
                fontSize: 'var(--text-2xl)',
                background: 'var(--gradient-orange)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
            }}>
                Transactions
            </h1>
            
            {/* Фильтры по дате */}
            <div style={{
                background: 'var(--medium-gray)',
                border: '1px solid var(--light-gray)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--spacing-lg)',
                marginBottom: 'var(--spacing-lg)',
                boxShadow: 'var(--shadow-card)',
                display: 'flex',
                gap: 'var(--spacing-lg)',
                alignItems: 'end'
            }}>
                <DatePicker 
                    label="From" 
                    value={startDate} 
                    onChange={handleSetStartDate}
                />
                <DatePicker 
                    label="To" 
                    value={endDate} 
                    onChange={handleSetEndDate}
                />
                
            </div>
            
            {paymentsList?.txRecords?.length > 0 ? (
                <div className="table-container payments-table-container" style={{ overflowX: 'auto' }}>
                    <table className="table" style={{ minWidth: '600px' }}>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Amount</th>
                                <th>Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paymentsList?.txRecords?.map((row, index) => (
                                <tr key={index}>
                                    <td>{moment(row?.time).format('L LT')}</td>
                                    <td>
                                        <TransactionChip type={row?.type} />
                                    </td>
                                    <td>{row?.memo?.replace(/(.{80})..+/, "$1…")}</td>
                                    <td style={{ 
                                        color: row?.amtC < 0 ? '#eb5d0c' : '#00B24A',
                                        fontWeight: '600'
                                    }}>
                                        {row?.amtC < 0 ? `-$${row?.amtC ? (Math.abs(row.amtC)/100).toFixed(2) : '0.00'}` : `+$${row?.amtC ? (row.amtC/100).toFixed(2) : '0.00'}`}
                                    </td>
                                    <td style={{ 
                                        color: '#1A53FF',
                                        fontWeight: '600'
                                    }}>
                                        ${row?.balC ? (row.balC/100).toFixed(2) : '0.00'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {/* Пагинация */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 'var(--spacing-lg)',
                        borderTop: '1px solid var(--light-gray)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    background: 'var(--medium-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '8px 12px',
                                    fontSize: 'var(--text-sm)'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                {isNaN(page) ? 0 : page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, paymentsList?.total || 0)} of {paymentsList?.total || 0}
                            </span>
                            <PaginationActions 
                                page={page}
                                rowsPerPage={rowsPerPage}
                                count={paymentsList?.total || 0}
                                onPageChange={handleChangePage}
                                loadingButton={loadingButton}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--spacing-2xl)',
                    background: 'var(--medium-gray)',
                    border: '1px solid var(--light-gray)',
                    borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-card)',
                    textAlign: 'center'
                }}>
                    <div style={{
                        fontSize: '48px',
                        marginBottom: 'var(--spacing-lg)'
                    }}>
                        💰
                    </div>
                    <h3 style={{
                        color: 'var(--text-light)',
                        fontSize: 'var(--text-lg)',
                        fontWeight: '600',
                        margin: '0 0 var(--spacing-sm) 0'
                    }}>
                        No transactions found
                    </h3>
                    <p style={{
                        color: 'var(--text-muted)',
                        fontSize: 'var(--text-sm)',
                        margin: '0',
                        lineHeight: '1.5'
                    }}>
                        You don't have any transaction records for the selected period.
                    </p>
                </div>
            )}
        </div>
    )
}

// Основной компонент с условным рендерингом
const PaymentsTable = (props) => {
    const { isMobile } = useMobile()


    // Если мобильная версия, используем мобильный компонент
    if (isMobile) {
        return <MobilePaymentsTable {...props} />
    }

    // Иначе используем десктопную версию
    return <DesktopPaymentsTable {...props} />
}

export default PaymentsTable